import React, { Component } from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import Styled from "styled-components";

import { FaSearchMinus as NoResultsIcon } from "react-icons/fa";
import { MdMoreHoriz as LoadMoreIcon } from "react-icons/md";
import LoadingSpinner from "../UI/LoadingSpinner";
import SearchIcon from "../../icons/SearchIcon";
import CloseIcon from "../../icons/CloseIcon";

import { search } from "../../api/BibleApi";
import { parseQueryString } from "../../utils/paths";
import AppBar from "../AppBar/AppBar";
import {
  PickerContainer,
  PickerGroup,
  StyledButtonItem,
  SearchButtonItem,
  AppBarSpacer
} from "../UI/Pickers";
import media from "../../utils/media";
import Viewer from "../UI/Viewer";

import SearchInput from "./SearchInput";
import Passage from "./Passage";
import Paginator from "./Paginator";
import withTracker from "../../utils/withTracker";
import { isMobileDevice } from "../../utils/mobile";
import { chapterPath } from "../../utils/paths";

const BottomGradient = Styled.div`
  display:block;
  position: absolute;
  top: 8em;
  width:100%;
  height: 4em;
  transition: background 0.6s ease;
  background: linear-gradient(transparent, rgba(255,255,255, 1));
`;

const SearchResult = Styled.div`
  position: relative;
  /* font-family: ${props => props.theme.bibleFont}; */
  font-family: 'Noto Serif', serif;
  margin: 0;

    // hide section header and refs in search resault
   .s, .s1, .s2, .s3, .s4, .s5, .r{
     display: none;
   }
  .scripture-styles, p {
     overflow: ${props => (props.verse ? "auto" : "hidden")};
    //  display: -webkit-box;
    //  -webkit-line-clamp: 5;
    //  -webkit-box-orient: vertical;
     max-height: ${props => (props.verse ? "none" : "8em")};
   }

  ${media.desktop`margin:0 64px;`};
  direction: ${props => props.theme.scriptDirection || "ltr"};
  border-radius: 2px;
  h3, p {
    font-size:16px;
    margin:0;
    padding:0;
  };
  h3 {
    margin-bottom:.3em;
    font-weight: 700;
  };
  a {
    color: ${props => props.theme.primaryText};
    display: block;
    padding: 16px;
    text-decoration: none;
    transition: background-color 0.6s ease;
    &:before {
      transition: background 0.6s ease;
      background: linear-gradient(transparent, rgba(255,0,255, 1));
    }
    &:hover {
      background-color: ${props => props.theme.hightlightColor};
      &:after {
        background: linear-gradient(transparent 10px, rgba(255,255,255, 0));
      }
    }
  };
`;

const LoadMore = Styled.div`
  width: 100%;
  margin-bottom: 1.5rem;
  text-align: center;
  svg {
    fill: ${props => props.theme.computedPrimary};
    border: solid 2px ${props => props.theme.computedPrimary};
    border-radius: 100px;
  }
`;

const StyledEmptyContent = Styled.div`
  display: flex;
  align-items: center;
  min-height: 300px;
  margin: auto;
  opacity: 0.3;
  svg {
    margin: auto;
  }
`;

const LoadingContainer = Styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
  width: 100%;
  LoadingSpinner {
    max-width: 50%;
  }
`;

class SearchResults extends Component {
  static propTypes = {
    bibleId: PropTypes.string.isRequired,
    history: PropTypes.shape({
      location: PropTypes.shape({
        search: PropTypes.string.isRequired
      }).isRequired,
      go: PropTypes.func.isRequired,
      push: PropTypes.func.isRequired
    }).isRequired
  };

  state = {
    results: { verses: [] },
    activePage: 1,
    resultsPerPage: 10,
    isLoading: false,
    canClose: false,
    embed: false
  };

  componentDidMount() {
    this.fetchSearchResults({ initialLoad: true });
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.activePage !== this.state.activePage ||
      prevProps.location.search !== this.props.location.search
    ) {
      this.fetchSearchResults();
      window.scrollTo(0, 0);
    }
  }

  fetchSearchResults({ initialLoad = false } = {}) {
    const { q: query } = parseQueryString(this.props.history.location.search);

    const { bibleId } = this.props;
    const offset = this.state.resultsPerPage * (this.state.activePage - 1);

    if (initialLoad) {
      const { embed, embedInit } = parseQueryString(
        this.props.history.location.search
      );

      let canClose = query !== undefined;
      if (embedInit !== undefined) {
        canClose = false;
      }

      this.setState({
        canClose,
        embed
      });
    }

    if (query) {
      this.setState({
        isLoading: true
      });
      console.log("query:", query);
      search({ bibleId, query, offset }).then(data => {
        this.setState({ results: data.data, isLoading: false });
      });
    }
  }

  fetchNextResults = () => {
    const { q: query } = parseQueryString(this.props.history.location.search);
    const { bibleId } = this.props;
    const { results } = this.state;
    const offset = results.verses.length;

    this.setState({
      isLoading: true
    });
    if (query) {
      this.setState({
        isLoading: true
      });
      search({ bibleId, query, offset }).then(data => {
        const newResults = results.verses.concat(data.data.verses);
        this.setState({
          results: {
            ...this.state.results,
            verses: newResults
          },
          isLoading: false
        });
      });
    }
  };

  handlePageChange = pageNumber => {
    const { results } = this.state;
    const totalPages = Math.ceil(results.total / results.limit);
    if (pageNumber <= totalPages && pageNumber > 0) {
      this.setState({ activePage: pageNumber });
    }
  };

  handleLoadMore = () => {
    this.fetchNextResults();
  };

  handleClose = () => {
    const { history } = this.props;
    history.goBack();
  };

  renderPagination = results => {
    const numOfPages = results ? Math.ceil(results.total / results.limit) : 0;
    const { isLoading } = this.state;

    if (isMobileDevice()) {
      return results.verses.length < results.total ? (
        <LoadMore>
          {isLoading ? (
            <LoadingSpinner size={30} />
          ) : (
            <LoadMoreIcon size={30} onClick={this.handleLoadMore} />
          )}
        </LoadMore>
      ) : null;
    }
    return numOfPages < 2 ? null : (
      <Paginator
        onPageChange={this.handlePageChange}
        numOfPages={numOfPages}
        currentPage={this.state.activePage}
      />
    );
  };

  render() {
    const { q: query } = parseQueryString(this.props.history.location.search);
    const { bibleId } = this.props;
    const { results, isLoading, canClose } = this.state;
    const verses = results ? results.verses : null;
    let passages = results ? results.passages : null;

    // fix for a bug in the api when valid, but non-existent passage id's are provided
    if (passages) {
      passages = passages.filter(passage => passage);
    }

    if (passages && passages.length === 1) {
      const passage = passages[0];
      this.props.history.push(
        chapterPath({
          bibleId,
          chapterId: passage.chapterIds[0],
          passageId: passage.orgId,
          location: this.props.location
        })
      );
      return null;
    }

    return (
      <Viewer>
        <AppBar showMenuButton light>
          <AppBarSpacer />

          <PickerContainer>
            <PickerGroup>
              <SearchButtonItem>
                <SearchIcon size={22} />
              </SearchButtonItem>
              <SearchInput />
              {canClose && (
                <StyledButtonItem onClick={this.handleClose} key="close-search">
                  <CloseIcon size={16} />
                </StyledButtonItem>
              )}
            </PickerGroup>
          </PickerContainer>
        </AppBar>
        <div>
          {verses &&
            verses.map((verse, index) => (
              <SearchResult key={`${verse.id}-${index}`} verse>
                <Link
                  to={chapterPath({
                    bibleId,
                    chapterId: verse.chapterId,
                    passageId: verse.orgId,
                    location: this.props.location
                  })}
                >
                  <h3>{verse.reference}</h3>
                  <p>{verse.text}</p>
                </Link>
              </SearchResult>
            ))}

          {passages &&
            passages.map((passage, index) => (
              <SearchResult key={`${passage}-${index}`} ref={this.setRef}>
                {passage.content.length > 520 && <BottomGradient />}
                <Passage bibleId={bibleId} passage={passage} link ellipsize />
              </SearchResult>
            ))}
          {results && results.total ? (
            this.renderPagination(results)
          ) : query && isLoading ? (
            <LoadingContainer>
              <LoadingSpinner size="80" />
            </LoadingContainer>
          ) : query && !(passages && passages.length > 0 && passages[0]) ? (
            <StyledEmptyContent>
              <NoResultsIcon size="300" />
            </StyledEmptyContent>
          ) : null}
        </div>
      </Viewer>
    );
  }
}

export default withRouter(withTracker(SearchResults));
